import { useState } from 'react';
import { CheckCircleOutlined } from '@mui/icons-material';
import {
	FormActions,
	FormCard,
	FormField,
	FormInput,
	FormSelect,
	FormSectionTitle,
} from '@practium/ui';
import { useGessagemTab } from '../../hooks/useGessagemTab';
import { SimpleDataTable } from '../Tables/SimpleDataTable/SimpleDataTable';
import { RESULT_TABLE_COLUMNS, mapResultTableRows } from '../../utils/correctionTableMapper';
import {
	APLICACAO_FORMA_OPTIONS,
	DOSE_SECUNDARIA_LABEL,
	getOptionLabel,
} from '../../constants/correction.constants';
import { PanelActions } from '../PanelActions/PanelActions';
import styles from './TabPanel.module.css';

interface GessagemTabProps {
	canEdit?: boolean;
}

export const GessagemTab = ({ canEdit = true }: GessagemTabProps) => {
	const {
		form,
		ajuste,
		result,
		hasCalculated,
		isCalculating,
		isSaving,
		errors,
		set,
		setAjuste,
		handleCalculate,
		handleSave,
		handleClear,
		conditionerOptions,
		soilAnalysisOptions,
		handleAdjustDose,
		isAdjusting,
	} = useGessagemTab();

	const [isSaved, setIsSaved] = useState(() => hasCalculated && !!form.condicionador);

	const doseSecLabel = form.forma ? DOSE_SECUNDARIA_LABEL[form.forma] : '';
	const isFixedDepth = form.forma === 'total' || form.forma === 'faixa';
	const showLargura = form.forma === 'sulco' || form.forma === 'faixa' || form.forma === 'cova';
	const showComprimento = form.forma === 'cova';

	const handleSaveAndView = async () => {
		const saved = await handleSave();
		if (saved) setIsSaved(true);
	};

	if (isSaved) {
		return (
			<div className={styles.panel}>
				<FormCard icon={<CheckCircleOutlined />} title="Gessagem">
					<div className={styles.form}>
						<div className={styles.section}>
							<FormSectionTitle>Cálculo</FormSectionTitle>
							<div className={styles.sectionFields}>
								<div className={styles.row2}>
									<FormField label="Análise de solo">
										<span className={styles.viewText}>
											{getOptionLabel(soilAnalysisOptions, form.soilAnalysis)}
										</span>
									</FormField>
									<FormField label="Interpretação">
										<span className={styles.viewText}>{form.interpretacao || '—'}</span>
									</FormField>
								</div>
								<div className={styles.row2}>
									<FormField label="Condicionador">
										<span className={styles.viewText}>
											{getOptionLabel(conditionerOptions, form.condicionador)}
										</span>
									</FormField>
									<FormField label="Saturação por Ca desejada (%)">
										<span className={styles.viewText}>{form.satCa2 || '—'}</span>
									</FormField>
								</div>
								<div className={styles.row2}>
									<FormField label="Forma de aplicação">
										<span className={styles.viewText}>
											{getOptionLabel(APLICACAO_FORMA_OPTIONS, form.forma)}
										</span>
									</FormField>
									{form.forma && (
										<FormField label="Profundidade">
											<span className={styles.viewText}>
												{isFixedDepth ? 'Superficial' : form.profundidade || '—'}
											</span>
										</FormField>
									)}
								</div>
								{showLargura && (
									<div className={showComprimento ? styles.row2 : styles.halfLeft}>
										<FormField label="Largura (m)">
											<span className={styles.viewText}>{form.largura || '—'}</span>
										</FormField>
										{showComprimento && (
											<FormField label="Comprimento (m)">
												<span className={styles.viewText}>{form.comprimento || '—'}</span>
											</FormField>
										)}
									</div>
								)}
							</div>
						</div>

						<div className={styles.section}>
							<FormSectionTitle>Ajuste</FormSectionTitle>
							<div className={styles.sectionFields}>
								<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
									<FormField label="Dose (kg/ha)">
										<span className={styles.viewText}>{ajuste.dose || '—'}</span>
									</FormField>
									{doseSecLabel && (
										<FormField label={doseSecLabel}>
											<span className={styles.viewText}>{ajuste.doseSecundaria || '—'}</span>
										</FormField>
									)}
								</div>
							</div>
						</div>

						{result && (
							<div className={styles.section}>
								<FormSectionTitle>Resultado</FormSectionTitle>
								<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(result.table)} />
							</div>
						)}
					</div>
				</FormCard>

				<PanelActions>
					<FormActions
						mode="view"
						onDelete={
							canEdit
								? () => {
										handleClear();
										setIsSaved(false);
									}
								: undefined
						}
						onEdit={canEdit ? () => setIsSaved(false) : undefined}
					/>
				</PanelActions>
			</div>
		);
	}

	return (
		<div className={styles.panel}>
			<FormCard icon={<CheckCircleOutlined />} title="Gessagem">
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Cálculo</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField label="Análise de solo" required error={errors.soilAnalysis}>
									<FormSelect
										value={form.soilAnalysis}
										onChange={(v) => set('soilAnalysis', v)}
										options={soilAnalysisOptions}
										placeholder="Selecione a análise"
										hasError={!!errors.soilAnalysis}
									/>
								</FormField>
								<FormField label="Interpretação">
									<FormInput
										value={form.interpretacao}
										readOnly
										className={styles.readonlyInput}
										placeholder="Selecione a análise primeiro"
									/>
								</FormField>
							</div>

							<div className={styles.row2}>
								<FormField label="Condicionador" required error={errors.condicionador}>
									<FormSelect
										value={form.condicionador}
										onChange={(v) => set('condicionador', v)}
										options={conditionerOptions}
										placeholder="Selecione o condicionador"
										hasError={!!errors.condicionador}
									/>
								</FormField>
								<FormField label="Saturação por Ca desejada (%)" required error={errors.satCa2}>
									<FormInput
										placeholder="Ex.: 40"
										type="number"
										min="0"
										max="100"
										value={form.satCa2}
										onChange={(e) => set('satCa2', e.target.value)}
										hasError={!!errors.satCa2}
									/>
								</FormField>
							</div>

							<div className={styles.row2}>
								<FormField label="Forma de aplicação" required error={errors.forma}>
									<FormSelect
										value={form.forma}
										onChange={(v) => {
											set('forma', v);
											set('profundidade', '');
											set('largura', '');
											set('comprimento', '');
										}}
										options={APLICACAO_FORMA_OPTIONS}
										placeholder="Selecione a aplicação"
										hasError={!!errors.forma}
									/>
								</FormField>
								{form.forma &&
									(isFixedDepth ? (
										<FormField label="Profundidade">
											<FormInput value="Superficial" readOnly className={styles.readonlyInput} />
										</FormField>
									) : (
										<FormField label="Profundidade (m)" required error={errors.profundidade}>
											<FormInput
												placeholder="Ex.: 0.44"
												type="number"
												value={form.profundidade}
												onChange={(e) => set('profundidade', e.target.value)}
												hasError={!!errors.profundidade}
											/>
										</FormField>
									))}
							</div>

							{showLargura && (
								<div className={showComprimento ? styles.row2 : styles.halfLeft}>
									<FormField label="Largura (m)" required error={errors.largura}>
										<FormInput
											placeholder="Ex.: 0.42"
											type="number"
											value={form.largura}
											onChange={(e) => set('largura', e.target.value)}
											hasError={!!errors.largura}
										/>
									</FormField>
									{showComprimento && (
										<FormField label="Comprimento (m)" required error={errors.comprimento}>
											<FormInput
												placeholder="Ex.: 0.8"
												type="number"
												value={form.comprimento}
												onChange={(e) => set('comprimento', e.target.value)}
												hasError={!!errors.comprimento}
											/>
										</FormField>
									)}
								</div>
							)}
						</div>
					</div>

					{hasCalculated && (
						<>
							<div className={styles.section}>
								<FormSectionTitle>Ajuste</FormSectionTitle>
								<div className={styles.sectionFields}>
									<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
										<FormField label="Dose (kg/ha)" required>
											<FormInput
												type="number"
												value={ajuste.dose}
												onChange={(e) => setAjuste('dose', e.target.value)}
												onBlur={handleAdjustDose}
												disabled={isAdjusting}
											/>
										</FormField>
										{doseSecLabel && (
											<FormField label={doseSecLabel}>
												<FormInput
													value={ajuste.doseSecundaria}
													readOnly
													className={styles.readonlyInput}
												/>
											</FormField>
										)}
									</div>
								</div>
							</div>

							{result && (
								<div className={styles.section}>
									<FormSectionTitle>Resultado</FormSectionTitle>
									<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(result.table)} />
								</div>
							)}
						</>
					)}
				</div>
			</FormCard>

			<PanelActions>
				<FormActions
					onClear={() => {
						handleClear();
						setIsSaved(false);
					}}
					onSecondary={handleCalculate}
					secondaryText={hasCalculated ? 'Recalcular' : 'Calcular'}
					secondaryDisabled={isCalculating}
					secondaryLoading={isCalculating}
					onSave={handleSaveAndView}
					isLoading={isSaving}
					saveDisabled={!hasCalculated}
				/>
			</PanelActions>
		</div>
	);
};
